import { CircularProgress } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import {
  AiOutlineArrowDown,
  AiOutlineArrowUp,
  AiOutlinePlusCircle,
} from 'react-icons/ai';
import { FaArrowsAltH } from 'react-icons/fa';
import { geradorDePLU } from '~/components/CadastroRapidoProduto/utils/geradorDePLU';
import { IconBetween } from '~/pages/Produto/Abas/AbaGeral/Plu/styles';
import { Icon, InputIconContainer } from '~/pages/Produto/styles';
import api from '~/services/api';

interface GeraPluProps {
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  codTela: number;
}

export const GeraPLU: React.FC<GeraPluProps> = ({
  setValue,
  getValues,
  codTela,
}) => {
  const [tipoPLU, setTipoPLU] = useState<0 | 1>(0); // 0 - PLU alto, // 1 - PLU baixo

  const [isLoading, setIsLoading] = useState(false);

  const storageKey = `USER_PREFERENCE_PLU_CADASTRO_RAPIDO_${codTela}`;

  const gerarPlu = useCallback(async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);

      const plu = await geradorDePLU(codTela);

      setValue('cod_produto', plu);

      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, setValue, tipoPLU]);

  const handleChangeTipoPLU = async (tipoPlu: number) => {
    setTipoPLU(tipoPlu === 0 ? 1 : 0);
    localStorage.setItem(storageKey, tipoPlu === 0 ? '1' : '0');

    setValue('cod_produto', '');

    await gerarPlu();
  };

  const handleBuscaPLUBaixo = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      const res = await api.get('/produto/plu-baixo');
      const { data, success } = res.data;

      if (success) {
        const { plu } = data;
        setValue('cod_produto', plu);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const userPreferencePluStorage = localStorage.getItem(storageKey);
    const tipo = userPreferencePluStorage
      ? parseInt(userPreferencePluStorage, 10)
      : 0;

    setTipoPLU(tipo as 0 | 1);
  }, [storageKey]);

  useEffect(() => {
    gerarPlu();
  }, []);

  // Ids estáveis p/ A11y
  const inputId = useMemo(() => `plu-input-${codTela}`, [codTela]);
  const labelId = useMemo(() => `plu-label-${codTela}`, [codTela]);

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <label id={labelId} htmlFor={inputId} style={{ marginBottom: 4 }}>
          Código PLU
        </label>

        <InputIconContainer>
          {tipoPLU === 0 && (
            <Icon
              className="icon-left"
              style={{
                backgroundColor: '#28a745',
                height: 'calc(100% - 6px)',
                left: 3,
                top: 3,
                cursor: 'pointer',
              }}
              disabled={isLoading}
              title="PLU Alto"
              onClick={async () => {
                await handleChangeTipoPLU(0);
              }}
              aria-label="Alternar para PLU Baixo"
            >
              <AiOutlineArrowUp style={{ color: '#fff' }} />
            </Icon>
          )}

          {tipoPLU === 1 && (
            <Icon
              className="icon-left"
              style={{
                backgroundColor: '#ffc107',
                height: 'calc(100% - 6px)',
                left: 3,
                top: 3,
                cursor: 'pointer',
              }}
              disabled={isLoading}
              title="PLU Baixo"
              onClick={async () => {
                await handleChangeTipoPLU(1);
              }}
              aria-label="Alternar para PLU Alto"
            >
              <AiOutlineArrowDown style={{ color: '#000' }} />
            </Icon>
          )}

          <input
            id={inputId}
            aria-labelledby={labelId}
            className="input input-produto"
            name="cod_produto"
            type="text"
            value={getValues('cod_produto') ?? ''}
            disabled
            style={{
              textAlign: 'right',
              padding: '0px 40px',
              backgroundColor: 'var(--ds-background-default,#EBECF0)',
              borderRadius: '3px',
              fontSize: '14px',
              border: '2px solid var(--ds-border-neutral, #DFE1E6)',
              height: 36,
            }}
          />

          <Icon
            className="icon-right"
            style={{
              backgroundColor: '#4C9AFF',
              height: 'calc(100% - 6px)',
              right: 3,
              top: 3,
              cursor: 'pointer',
            }}
            disabled={isLoading}
            onClick={async () => {
              await gerarPlu();
            }}
            title="Gerar PLU"
            aria-label="Gerar novo PLU"
          >
            {isLoading ? (
              <CircularProgress size={16} style={{ color: '#fff' }} />
            ) : (
              <AiOutlinePlusCircle style={{ color: '#fff' }} size={24} />
            )}
          </Icon>
        </InputIconContainer>
      </div>

      {tipoPLU === 1 && (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="">Busca o Primeiro PLU Baixo Disponível</Tooltip>
          }
        >
          <IconBetween
            style={{
              cursor: 'pointer',
              color: '#8850bf',
              backgroundColor: 'transparent',
              border: '1px solid #8850bf',
              marginLeft: 5,
              marginTop: 23.5,
            }}
            disabled={isLoading}
            onClick={async () => {
              await handleBuscaPLUBaixo();
            }}
            aria-label="Buscar primeiro PLU baixo disponível"
            title="Buscar primeiro PLU baixo disponível"
          >
            {isLoading ? (
              <CircularProgress size={14} />
            ) : (
              <FaArrowsAltH fontSize={14} size={14} />
            )}
          </IconBetween>
        </OverlayTrigger>
      )}
    </div>
  );
};
